import { useState, createContext, useContext, useEffect } from 'react';
import { Analytics, AnalyticsBrowser } from '@segment/analytics-next';
import { decodeBase64EncodedStringAndReturnId } from 'helpers/common';

type AnalyticEventType = {
  event: string;
  source: string;
  disablePrefix?: boolean;
  eventData?: Record<string, any>;
};

type IdentifyUserType = {
  id: string;
  source: string;
  data?: Record<string, any>;
};

type UseAnalyticContextType = {
  trackAnalyticEvent: (analyticData: AnalyticEventType) => void;
  identifyUser: (identityData: IdentifyUserType) => void;
  decodeId: (id: string) => string;
};

export const AnalyticContext = createContext<UseAnalyticContextType>({
  trackAnalyticEvent: () => {},
  identifyUser: () => {},
  decodeId: () => '',
});

export type AnalyticsTest = {
  track: (event: string, properties: Record<string, any>) => void;
  identify: (id: string, properties: Record<string, any>) => void;
};

export const AnalyticProvider: React.FC<{ testMode?: AnalyticsTest }> = ({
  children,
  testMode,
}) => {
  const [Analytic, setAnalytic] = useState<Analytics | undefined>(undefined);
  const writeKey = process.env.SEGMENT_KEY || '';

  useEffect(() => {
    if (testMode) return;
    const loadAnalytic = async (): Promise<void> => {
      try {
        const [response] = await AnalyticsBrowser.load({ writeKey });
        setAnalytic(response);
      } catch (e) {
        console.error('Unable to load analytics', e); // eslint-disable-line no-console
      }
    };
    loadAnalytic();
  }, [writeKey]);

  const trackAnalyticEvent = ({
    event,
    source,
    disablePrefix = false,
    eventData = {},
  }: AnalyticEventType): void => {
    if (!event)
      throw new Error(
        `No analytics event name was provided to trackAnalyticsEvent`,
      );
    if (!source)
      throw new Error(
        `No analytics source was provided to trackAnalyticsEvent`,
      );

    // if (!Object.values(events).find((val) => val === event))
    //   throw new Error(
    //     `Analytic event "${event}" is not a defined analytics event.`,
    //   );

    const properties = {
      source: disablePrefix ? source : `web-${source}`,
      origin: window.location.href,
      ...eventData,
      platform: 'web',
    };

    if (testMode) {
      testMode.track(event, properties);
      return;
    }

    Analytic?.track?.(event, properties);
  };

  const decodeId = (id: string): string => {
    try {
      return id && decodeBase64EncodedStringAndReturnId(id);
    } catch (e) {
      return id;
    }
  };

  const identifyUser = ({ id, source, data }: IdentifyUserType): void => {
    if (testMode) {
      testMode.identify(id, {
        source,
        ...data,
      });
      return;
    }
    Analytic?.identify(decodeId(id), {
      source,
      ...data,
    });
  };

  return (
    <AnalyticContext.Provider
      value={{ trackAnalyticEvent, identifyUser, decodeId }}
    >
      {children}
    </AnalyticContext.Provider>
  );
};

export const useAnalytics = (): UseAnalyticContextType =>
  useContext(AnalyticContext);
